import type { AsyncOperationType } from '../dto/asyncOperationType';
import {
  GeneralPermissions, FolderPermissions, ItemPermissions,
  PersonPermissions, Roles, UserManagementPermissions,
  SystemActivityPermissions,
  SharePermissions,
  ClassifiedAllTenantsPermissions,
  UIOnlyPermissions,
  fetchlogsPermissions,
  TaskPermissions,
} from './permissions';
import type { PermissionTabName } from '../types/UIOnlySections';

const hasPermissionOrIsSuperAdmin = (userPermissions: string[], permissionToCheck: string): boolean =>
  hasPermission(userPermissions, permissionToCheck) || isSuperAdmin(userPermissions);

const hasPermission = (userPermissions: string[], permissionToCheck: string): boolean => {
  return userPermissions.includes(permissionToCheck);
};

export const isSuperAdmin = (userPermissions: string[]): boolean =>
  userPermissions.includes(Roles.superAdmin);

export const isTenantAdmin = (userPermissions: string[]): boolean =>
  userPermissions.includes(Roles.tenantAdmin) || userPermissions.includes(UserManagementPermissions.tenantInfo.permission);

export const hasAllTenantsBillingAccess = (userPermissions: string[]): boolean =>
  hasPermissionOrIsSuperAdmin(userPermissions, ClassifiedAllTenantsPermissions.billing.permission);

export const hasAllTenantsWriteAccess = (userPermissions: string[]): boolean =>
  hasPermissionOrIsSuperAdmin(userPermissions, ClassifiedAllTenantsPermissions.write.permission);

export const hasAllTenantsTunnelAccess = (userPermissions: string[]): boolean =>
  hasPermissionOrIsSuperAdmin(userPermissions, ClassifiedAllTenantsPermissions.tunnel.permission);

export const hasAllTenantsKeldaAccess = (userPermissions: string[]): boolean =>
  hasPermissionOrIsSuperAdmin(userPermissions, ClassifiedAllTenantsPermissions.kelda.permission);

export const isSupportTeam = (userPermissions: string[]): boolean =>
  userPermissions.includes(Roles.supportTeam);

export const hasTenantInfoAccess = (userPermissions: string[]): boolean =>
  isTenantAdmin(userPermissions) ||
  hasPermissionOrIsSuperAdmin(userPermissions, UserManagementPermissions.tenantInfo.permission);

export const hasFetchlogsPermissions = (userPermissions: string[]): boolean => {
  for (const p of fetchlogsPermissions) {
    if (!hasPermission(userPermissions, p.permission)) {
      return false;
    }
  }
  return true;
};

export const canPerformAnalyticsOperation = (userPermissions: string[], operationType: AsyncOperationType) => {
  return hasPermissionOrIsSuperAdmin(userPermissions, getPermissionToCheckForAnalyticOp(operationType));
}

export const canPerformOwnTenantSupport = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, UserManagementPermissions.tenantSupport.permission);

export const isOnlyOwnTenantSupportUser = (userPermissions: string[]) =>
  userPermissions.includes(UserManagementPermissions.tenantSupport.permission) && !isSuperAdmin(userPermissions) && !isSupportTeam(userPermissions);

export const canCreateItem = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.create.permission);

export const canUploadItemOnClient = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.upload.permission);

export const canExportSequenceOnClient = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.exportSequence.permission);

export const canUploadSequenceDependenciesInClient = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.dependenciesOnExportSequence.permission);

export const canRestoreMedia = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.restoreMedia.permission);

export const canDeleteItem = (userPermissions: string[], createdBy?: string, userId?: string) => {
  if (createdBy && userId && createdBy === userId) return true;
  return hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.delete.permission);
};

export const canViewTab = (userPermissions: string[], tab: PermissionTabName) => {
  return hasPermission(userPermissions, UIOnlyPermissions[tab].permission);
};

export const canRecoverDeletedItem = (userPermissions: string[], createdBy?: string, userId?: string) => {
  if (createdBy && userId && createdBy === userId) return true;
  return hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.recoverDeleted.permission);
};

export const canUpdateItem = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.update.permission);

export const canReprocessItem = (userPermissions: string[]) =>
  isTenantAdmin(userPermissions) || hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.reprocess.permission);

export const canMoveItems = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.move.permission);

export const canViewItemHistory = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.history.permission);

export const canChangeOwnerOfItem = (userPermissions: string[]) =>
  isTenantAdmin(userPermissions) || hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.changeOwner.permission);

export const canUpdateItemMetadata = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.updateMetadata.permission);

export const canUpdateOwnItemMetadata = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.updateOwnMetadata.permission);

export const canMakeVisibleToSelf = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, GeneralPermissions.setVisibleToSelf.permission);

export const canMakeVisibleToGroups = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, GeneralPermissions.setVisibleToGroups.permission);

export const canDoBulkMetadataUpdate = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, GeneralPermissions.bulkMetadataUpdate.permission);

export const canCopyMedia = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.copyMedia.permission);

export const canUpdateItemTranscript = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.updateTranscript.permission);

export const canCreateVirtualClip = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.createVirtualClip.permission);

export const canDownloadHighResMedia = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.downloadMedia.permission);

export const canArchiveItem = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.archive.permission);

export const canUnarchiveItem = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.unarchive.permission);

export const canCreatePlaceholder = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.createPlaceholder.permission);

export const canEditItemRestrictions = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.editRestrictions.permission);

export const canManageLiveRecording = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.createCloudLive.permission);

export const canCreateLiveRecordingInExistingPlaceholder = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.createCloudLiveInExistingPlaceholder.permission);

export const canChooseUploadLocation = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.chooseUploadLocation.permission);

export const canSendToStorage = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.sendToStorage.permission);

export const canControlPlayout = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, GeneralPermissions.playout.permission);

export const canControlPlayoutDebug = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, GeneralPermissions.playoutDebug.permission);

export const canCreatePerson = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, PersonPermissions.create.permission);

export const canDeletePerson = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, PersonPermissions.delete.permission);

export const canMergePersons = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, PersonPermissions.merge.permission);


export const canUpdatePerson = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, PersonPermissions.update.permission);

export const canCreateFolder = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, FolderPermissions.create.permission);

export const canDeleteFolder = (userPermissions: string[], owner?: string, userId?: string) => {
  if (owner && userId && owner === userId) return true;
  return hasPermissionOrIsSuperAdmin(userPermissions, FolderPermissions.delete.permission);
};

export const canUpdateFolder = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, FolderPermissions.update.permission);

export const canShareMedia = (userPermissions: string[]) =>
  hasPermission(userPermissions, SharePermissions.shareMedia.permission);

export const canRequestUpload = (userPermissions: string[]) =>
  hasPermission(userPermissions, SharePermissions.requestUpload.permission);

export const canReadComments = (userPermissions: string[]) =>
  hasPermission(userPermissions, ItemPermissions.commentRead.permission);

export const canCreateComment = (userPermissions: string[]) =>
  hasPermission(userPermissions, ItemPermissions.commentWrite.permission);

export const canModifyCommentStatus = (userPermissions: string[]) =>
  hasPermission(userPermissions, ItemPermissions.commentStatus.permission);

export const canDeleteOwnComment = (userPermissions: string[]) =>
  hasPermission(userPermissions, ItemPermissions.commentDeleteOwn.permission);

export const canDeleteOthersComment = (userPermissions: string[]) =>
  hasPermission(userPermissions, ItemPermissions.commentDeleteOthers.permission);

export const canListAllTasks = (userPermissions: string[]) =>
  hasPermission(userPermissions, TaskPermissions.listAll.permission);

export const canRelateItem = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.relation.permission);

export const canVersionItem = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.version.permission);

export const canPromoteItem = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.promote.permission);

export const canCreateVersionRegardlessOfUpdateControl = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.createVersionRegardlessOfUpdateControl.permission);

export const canDownloadMarkers = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, ItemPermissions.downloadMarkers.permission);

export const canViewOnGoingDownloads = (userPermissions: string[]) =>
  isTenantAdmin(userPermissions) || hasPermissionOrIsSuperAdmin(userPermissions, SystemActivityPermissions.download.permission);

export const canViewKeldaActivities = (userPermissions: string[]) =>
  isTenantAdmin(userPermissions) || hasPermissionOrIsSuperAdmin(userPermissions, SystemActivityPermissions.processor.permission);

export const canViewUploadActivities = (userPermissions: string[]) =>
  isTenantAdmin(userPermissions) || hasPermissionOrIsSuperAdmin(userPermissions, SystemActivityPermissions.upload.permission);

export const canCreateApiKeys = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, GeneralPermissions.apiKeys.permission);

export const canGetGroups = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, GeneralPermissions.getGroups.permission);

export const canViewBulkOperations = (userPermissions: string[]) => isSuperAdmin(userPermissions);

const getPermissionToCheckForAnalyticOp = (operationType: AsyncOperationType) => {
  switch (operationType) {
    case 'archiveFileTransfer':
      return ItemPermissions.archive.permission;

    default:
      return `analytics/${operationType}`;
  }
};

export const canViewFilter = (permissions : string[]) => {
  return hasPermission(permissions, UIOnlyPermissions.searchFilter.permission);
};

export const shouldShowBulkMetadataUpdateOption = (permissions: string[]) => {
  return canDoBulkMetadataUpdate(permissions) && canUpdateItemMetadata(permissions);
};

export const canManageSystemBookmarks = (userPermissions: string[]) =>
  hasPermissionOrIsSuperAdmin(userPermissions, GeneralPermissions.bookmarks.permission);

export const canViewSharePage = (permissions: string[]) => {
  return canShareMedia(permissions) || canRequestUpload(permissions);
};

export function canAddAllTenantsPermissions(userPermissions: string[], tenantId: string) {
  return isSuperAdmin(userPermissions) && tenantId === 'mimiradmin';
}

export const canViewMimirCutter = (userPermissions: string[]) => {
  return hasPermission(userPermissions, GeneralPermissions.mimirCutter.permission)
    && hasPermission(userPermissions, ItemPermissions.create.permission);
};

export const canViewExperimentalCutter = (userPermissions: string[]) => {
  return hasPermission(userPermissions, UIOnlyPermissions.cutter.permission)
    && hasPermission(userPermissions, ItemPermissions.create.permission);
}

export function isItemOwnerAndCanUpdateOwnItemMetadata(
  itemCreatedBy: string | undefined,
  userPermissions: string[],
  userId: string,
) {
  return itemCreatedBy === userId && canUpdateOwnItemMetadata(userPermissions);
}
